import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { UserButton } from "@clerk/react";
import { ChartLineUp, PlugsConnected, CreditCard, EnvelopeSimple, BellRinging, ChartBar, UserCircleGear, CaretDoubleLeft, CaretDoubleRight, UsersThree } from "@phosphor-icons/react";
import { JoinTeam, hasJoinToken, ViewerBanner, TeamPaused, MfaSetupRequired } from "@/components/app/TeamGates";
import { Logo, LogoMark } from "@/components/Logo";
import { TrialBanner } from "@/components/app/TrialBanner";
import { RunwayAlert } from "@/components/app/RunwayAlert";
import { useHealth } from "@/components/integrations/Health";
import { useMe } from "@/components/app/TrialBanner";
import { TermsGate, TrialEnded } from "@/components/app/AccessGates";
import { MfaChallenge } from "@/components/account/MfaChallenge";
import { DemoBar, DemoStartButton } from "@/components/app/DemoMode";
import { DemoTour } from "@/components/app/DemoTour";

const LOCKED_PAGES = ["/app/billing", "/app/account"];
const LockedOutlet = () => {
  const { pathname } = useLocation();
  return LOCKED_PAGES.some((p) => pathname.startsWith(p)) ? <Outlet /> : null;
};

const LINKS = [
  ["/app", "Dashboard", ChartLineUp, "nav-dashboard"],
  ["/app/integrations", "Integrations", PlugsConnected, "nav-integrations"],
  ["/app/billing", "Billing", CreditCard, "nav-billing"],
  ["/app/alerts", "Alerts & reports", BellRinging, "nav-alerts"],
  ["/app/team", "Team", UsersThree, "nav-team"],
  ["/app/account", "Account & privacy", UserCircleGear, "nav-account"],
];
const ADMIN_LINKS = [["/app/emails", "Onboarding Emails", EnvelopeSimple, "nav-emails"], ["/app/admin/usage", "Limit Hits", ChartBar, "nav-admin-usage"]];
const KEY = "cr-nav-collapsed";

const useLinks = (enabled, member, demo) => {
  const api = useApi();
  const { data } = useQuery({ queryKey: ["admin-status"], queryFn: () => api.get("/admin/status"), staleTime: Infinity, enabled });
  const base = member || demo ? LINKS.filter(([to]) => to !== "/app/billing") : LINKS;
  return [data?.is_admin && !demo ? [...base, ...ADMIN_LINKS] : base, !!data?.can_demo, !!data?.is_admin];
};

const useCollapsed = () => {
  const [c, setC] = useState(() => localStorage.getItem(KEY) === "1");
  const toggle = () => setC((v) => { localStorage.setItem(KEY, v ? "0" : "1"); return !v; });
  return [c, toggle];
};

const NavDot = ({ tid, count, slim }) => (tid === "nav-integrations" && count > 0
  ? <span data-testid="nav-integrations-alert" title={`${count} connection${count > 1 ? "s" : ""} need reconnecting`}
      className={`grid h-4 min-w-4 place-items-center rounded-full bg-[#FF5C7A] px-1 font-mono text-[10px] font-bold text-[#050505] ${slim ? "absolute -top-1 -right-1" : "ml-auto"}`}>{count}</span>
  : null);

const MiniLogo = () => (
  <NavLink to="/" data-testid="brand-logo" className="h-8 w-8 grid place-items-center" title="CalibRunway" aria-label="CalibRunway home">
    <LogoMark className="h-8 w-8" />
  </NavLink>
);

const Sidebar = ({ links, broken, canDemo }) => {
  const [slim, toggle] = useCollapsed();
  return (
    <aside data-testid="sidebar" data-collapsed={slim ? "1" : "0"}
      className={`hidden md:flex shrink-0 flex-col border-r border-[#2A2A2A] sticky top-0 h-screen transition-[width] duration-200 ${slim ? "w-[72px] p-3 items-center" : "w-60 p-5"}`}>
      <div className={`flex items-center ${slim ? "flex-col gap-4" : "justify-between"}`}>
        {slim ? <MiniLogo /> : <Logo />}
        <button data-testid="sidebar-toggle" onClick={toggle} title={slim ? "Expand menu" : "Collapse menu"} aria-label={slim ? "Expand menu" : "Collapse menu"}
          className="grid h-7 w-7 place-items-center rounded-md text-[#6B6B6B] hover:text-white hover:bg-[#121212] transition-colors">
          {slim ? <CaretDoubleRight size={14} /> : <CaretDoubleLeft size={14} />}
        </button>
      </div>
      <nav className={`flex flex-col gap-1 ${slim ? "mt-8 items-center" : "mt-10"}`}>
        {links.map(([to, label, Icon, tid]) => (
          <NavLink key={to} to={to} end data-testid={tid} title={slim ? label : undefined}
            className={({ isActive }) => `relative flex items-center rounded-lg text-sm transition-colors ${slim ? "h-10 w-10 justify-center" : "gap-3 px-3 py-2.5"} ${isActive ? "bg-[#00FF9D]/10 text-[#00FF9D]" : "text-[#A3A3A3] hover:text-white hover:bg-[#121212]"}`}>
            <Icon size={18} />{!slim && label}<NavDot tid={tid} count={broken} slim={slim} />
          </NavLink>
        ))}
        {canDemo && <DemoStartButton slim={slim} />}
      </nav>
      <div className="mt-auto flex items-center gap-3" data-testid="user-menu"><UserButton />{!slim && <span className="text-xs text-[#A3A3A3]">Account</span>}</div>
    </aside>
  );
};

export default function AppShell() {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const pre = me && !me.mfa_required && !hasJoinToken();
  const gated = pre && !me.terms_accepted;
  const setupMfa = pre && me.terms_accepted && me.mfa_setup_required;
  const locked = pre && me.terms_accepted && !setupMfa && me.access === "locked";
  const paused = pre && me.terms_accepted && !setupMfa && me.access === "team_paused";
  const open = !!pre && !gated && !setupMfa && !locked && !paused;
  const member = !!me && me.role !== "owner";
  const demo = !!me?.demo;
  const [links, canDemo] = useLinks(open, member, demo);
  const broken = useHealth(open)?.needs_reconnect?.length || 0;
  return (
    <div className="min-h-screen flex bg-[#050505]">
      <Sidebar links={links} broken={broken} canDemo={open && canDemo && !member && !demo} />
      <div className="flex-1 min-w-0 overflow-x-hidden">
        {demo && <DemoBar company={me.company} />}
        {demo && pathname === "/app" && <DemoTour />}
        <header className="md:hidden sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10 px-4 h-14 flex items-center justify-between">
          <Logo testid="brand-logo-mobile" />
          <UserButton />
        </header>
        <nav className="md:hidden flex overflow-x-auto border-b border-[#2A2A2A] px-2">
          {links.map(([to, label, , tid]) => (
            <NavLink key={to} to={to} end data-testid={`m-${tid}`}
              className={({ isActive }) => `px-3 py-3 text-xs whitespace-nowrap ${isActive ? "text-[#00FF9D]" : "text-[#A3A3A3]"}`}>{label}{tid === "nav-integrations" && broken > 0 && <span data-testid="m-nav-integrations-alert" className="ml-1 inline-block h-2 w-2 rounded-full bg-[#FF5C7A]" />}</NavLink>
          ))}
          {open && canDemo && !member && !demo && <DemoStartButton slim={false} testid="m-demo-start-btn" className="px-3 py-3 text-xs whitespace-nowrap text-[#D9A6FF] flex items-center gap-1.5" />}
        </nav>
        {!member && <TrialBanner />}
        {open && me.role === "viewer" && <ViewerBanner />}
        {open && <RunwayAlert />}
        <main className="p-4 md:p-8">
          {!me ? null : me.mfa_required ? <MfaChallenge method={me.mfa_method} uid={me.user_id} /> : hasJoinToken() ? <JoinTeam />
            : gated ? (pathname.startsWith("/app/account") ? <Outlet /> : <TermsGate />)
            : setupMfa ? <MfaSetupRequired />
            : locked ? <><TrialEnded plan={me.plan} member={member} /><LockedOutlet /></>
            : paused ? (pathname.startsWith("/app/account") ? <Outlet /> : <TeamPaused />) : <Outlet />}
        </main>
      </div>
    </div>
  );
}
